import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, ChevronDown, ChevronUp, MessageCircle, Car, Mountain, Utensils, Snowflake } from 'lucide-react';
import SEO from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';

const FAQ_COUNT = 18;

const CATEGORY_IDS = [
  { id: 'tous',      icon: HelpCircle },
  { id: 'logement',  icon: Mountain   },
  { id: 'activites', icon: Snowflake  },
  { id: 'services',  icon: Utensils   },
  { id: 'pratique',  icon: Car        },
];

const getCategoryLabel = (id, t) => {
  if (id === 'tous') return t('faq.all');
  return t(`faq.cat.${id}`);
};

const FAQItem = React.memo(({ faq, isOpen, onToggle }) => {
  const Icon = isOpen ? ChevronUp : ChevronDown;

  return (
    <div className="bg-base-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
      <button
        onClick={() => onToggle(faq.id)}
        className="w-full p-6 text-left flex justify-between items-center gap-4 group"
        aria-expanded={isOpen}
      >
        <span className="text-base font-semibold group-hover:text-primary transition-colors">
          {faq.question}
        </span>
        <Icon className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'text-primary' : ''}`} />
      </button>

      <div
        className={`transition-all duration-300 overflow-hidden
          ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="px-6 pb-6 pt-2 bg-base-100 border-t border-base-300">
          {faq.answer.split('\n\n').map((para, i) => (
            <p key={i} className={`text-base-content/80 whitespace-pre-line ${i > 0 ? 'mt-3' : ''}`}>
              {para}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
});

const CategoryButton = React.memo(({ categoryId, icon: Icon, label, isActive, onClick }) => (
  <button
    onClick={() => onClick(categoryId)}
    className={`btn btn-sm gap-2 transition-all duration-300
      ${isActive ? 'btn-primary' : 'btn-ghost hover:bg-base-300'}`}
    aria-pressed={isActive}
  >
    <Icon className="w-4 h-4" />
    {label}
  </button>
));

const FAQ = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [activeCategory, setActiveCategory] = useState('tous');
  const [openId, setOpenId] = useState(null);
  const pageRef = useRef(null);

  const faqs = useMemo(() => {
    const items = [];
    for (let i = 1; i <= FAQ_COUNT; i++) {
      items.push({
        id: i,
        category: t(`faq.q${i}.category`),
        question: t(`faq.q${i}.question`),
        answer: t(`faq.q${i}.answer`),
      });
    }
    return items;
  }, [t]);

  const structuredData = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  }), [faqs]);

  useEffect(() => {
    if (pageRef.current) {
      const timer = setTimeout(() => setIsVisible(true), 300);
      return () => clearTimeout(timer);
    }
  }, []);

  const toggleFaq = useCallback((id) => {
    setOpenId(prev => prev === id ? null : id);
  }, []);

  const handleCategoryChange = useCallback((categoryId) => {
    setActiveCategory(categoryId);
    setOpenId(null);
  }, []);

  const filteredFaqs = useMemo(() =>
    activeCategory === 'tous'
      ? faqs
      : faqs.filter(faq => faq.category === activeCategory),
    [activeCategory, faqs]
  );

  return (
    <>
      <SEO
        title={t('seo.faq.title')}
        description={t('seo.faq.description')}
        keywords="faq la grave, questions hébergement, réservation appartement, informations séjour, marmotte joyeuse"
        url="/about"
        structuredData={structuredData}
      />
      <div
        ref={pageRef}
        className="container mx-auto px-4 pt-28 pb-16 min-h-screen"
      >
        <div className={`text-center mb-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h1 className="text-5xl font-extrabold mb-4 text-primary">{t('faq.title')}</h1>
          <p className="text-xl max-w-2xl mx-auto text-base-content/80">
            {t('faq.subtitle')}
          </p>
          <div className="h-1 w-32 bg-primary mx-auto mt-8" />
        </div>

        <div className={`mb-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex flex-wrap justify-center gap-2">
            {CATEGORY_IDS.map((cat) => (
              <CategoryButton
                key={cat.id}
                categoryId={cat.id}
                icon={cat.icon}
                label={getCategoryLabel(cat.id, t)}
                isActive={activeCategory === cat.id}
                onClick={handleCategoryChange}
              />
            ))}
          </div>
        </div>

        <div className={`max-w-3xl mx-auto space-y-3 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {filteredFaqs.map((faq) => (
            <FAQItem
              key={faq.id}
              faq={faq}
              isOpen={openId === faq.id}
              onToggle={toggleFaq}
            />
          ))}
        </div>

        <div className={`mt-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="bg-primary text-primary-content p-8 rounded-3xl shadow-xl max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-3">{t('faq.contactCta')}</h2>
            <p className="mb-6 text-primary-content/90">
              {t('faq.contactSub')}
            </p>
            <button
              onClick={() => navigate('/contact')}
              className="btn bg-white text-primary hover:bg-white/90 gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              {t('faq.contactLink')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default React.memo(FAQ);
